import { useEffect, useState, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { debounce } from 'lodash';

import { FHSmartTextarea } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { FORTA_ERROR, FORTA_YELLOW } from '../../../../../utils/colors';
import { Themes, MINIMUM_REQUIRED_SENTENCE_LENGTH } from '../../../../../constants';
import { GoalTypes } from '../common/constants/general';
import { targetDescriptionBlacklist } from '../../../../../pages/constants';
import BaselineAndMasteryCriteria from '../common/components/baselineAndMasteryCriteria';

import type { TreatmentPlanResourceVersion } from '../common/constants/general';
import type { ITreatmentPlanGoal } from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface GoalDescriptionsProps {
  goal: ITreatmentPlanGoal;
  treatmentPlanId: number;
  errors: any;
  goalVersion: TreatmentPlanResourceVersion;
  goalIndex: number;
  onUpdateField: (key: string, value: any) => void;
}

function GoalDescriptions(props: GoalDescriptionsProps) {
  const { goal, errors, goalIndex, treatmentPlanId, goalVersion, onUpdateField } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [errorState, setErrorState] = useState<any>(null);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [descriptionValues, setDescriptionValues] = useState({
    operational_definition: '',
    objective: '',
    antecedent_modifications: '',
    consequence_modifications: '',
    description: '',
  });

  useEffect(() => {
    if (!errors || !errors?.description || errorState?.description) return;
    setErrorState({ ...errorState, description: true });
  }, [errors]);

  useEffect(() => {
    if (!goal) return;
    // The TP must be editable
    setIsEditable(isTpEditable);
    setDescriptionValues({
      description: goal.description || '',
      operational_definition: goal.operational_definition || '',
      objective: goal.objective || '',
      antecedent_modifications: goal.antecedent_modifications || '',
      consequence_modifications: goal.consequence_modifications || '',
    });
  }, [isTpEditable, goal]);

  // Debounced update function for text fields
  const debouncedUpdateField = useMemo(
    () =>
      debounce((field: string, value: string) => {
        onUpdateField(field, value);
      }, 500),
    [onUpdateField],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateField.cancel();
    };
  }, [debouncedUpdateField]);

  const handleTextFieldChange = (field: string, value: string, updateRedux = true) => {
    // Update local state immediately
    setDescriptionValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (updateRedux) {
      // Debounce the Redux update
      debouncedUpdateField(field, value);
    }
  };

  const handleTextFieldBlur = (field: string, value: string) => {
    // Cancel any pending debounced updates
    debouncedUpdateField.cancel();
    // Update Redux immediately on blur
    onUpdateField(field, value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
      <BaselineAndMasteryCriteria
        goal={goal}
        treatmentPlanId={treatmentPlanId}
        isEditable={isEditable}
        onUpdateField={onUpdateField}
        errors={errors}
        goalIndex={goalIndex}
        goalVersion={goalVersion}
      />
      {goal.goal_type === GoalTypes.BehaviorReduction && (
        <Grid container spacing={3} mt={goal?.is_editing ? 1 : 0} sx={{ wordBreak: 'break-word' }}>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Operational definition* (Make sure to include onset/offset)
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_operational_definition-${goal.id}`}
                data-testid="operationalDefinitionTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter operational definition"
                value={descriptionValues.operational_definition}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  handleTextFieldChange('operational_definition', e.target.value, false);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      operational_definition: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      operational_definition: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  handleTextFieldBlur('operational_definition', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  handleTextFieldBlur(
                    'operational_definition',
                    descriptionValues.operational_definition,
                  );
                }}
                error={
                  (errorState && errorState['operational_definition']) ||
                  !descriptionValues.operational_definition
                }
                helperText={
                  errorState && errorState['operational_definition']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{descriptionValues.operational_definition}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Objective*
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_objective-${goal.id}`}
                data-testid="ObjectiveTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter objective"
                value={descriptionValues.objective}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  handleTextFieldChange('objective', e.target.value, false);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      objective: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      objective: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  handleTextFieldBlur('objective', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  handleTextFieldBlur('objective', descriptionValues.objective);
                }}
                error={(errorState && errorState['objective']) || !descriptionValues.objective}
                helperText={
                  errorState && errorState['objective']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{descriptionValues.objective}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Antecedent modifications*
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_antecedent_modifications-${goal.id}`}
                data-testid="antecedentModificationsTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter antecedent modifications"
                value={descriptionValues.antecedent_modifications}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  handleTextFieldChange('antecedent_modifications', e.target.value, false);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      antecedent_modifications: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      antecedent_modifications: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  handleTextFieldBlur('antecedent_modifications', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  handleTextFieldBlur(
                    'antecedent_modifications',
                    descriptionValues.antecedent_modifications,
                  );
                }}
                error={
                  (errorState && errorState['antecedent_modifications']) ||
                  !descriptionValues.antecedent_modifications
                }
                helperText={
                  errorState && errorState['antecedent_modifications']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{descriptionValues.antecedent_modifications}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Consequence modifications*
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_consequence_modifications-${goal.id}`}
                data-testid="consequenceModificationsTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter consequence modifications"
                value={descriptionValues.consequence_modifications}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  handleTextFieldChange('consequence_modifications', e.target.value, false);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      consequence_modifications: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      consequence_modifications: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  handleTextFieldBlur('consequence_modifications', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  handleTextFieldBlur(
                    'consequence_modifications',
                    descriptionValues.consequence_modifications,
                  );
                }}
                error={
                  (errorState && errorState['consequence_modifications']) ||
                  !descriptionValues.consequence_modifications
                }
                helperText={
                  errorState && errorState['consequence_modifications']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{descriptionValues.consequence_modifications}</Typography>
            )}
          </Grid>
        </Grid>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
        <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
          Goal description / rationale* (If re-auth current level is required)
        </Typography>
        {goal?.is_editing ? (
          <>
            {(descriptionValues.description?.includes('{') ||
              descriptionValues.description?.includes('}')) && (
              <Typography
                sx={{ color: FORTA_ERROR, fontWeight: 700, textAlign: 'left', mt: 1, mb: 0.5 }}
              >
                All curly brackets need to be removed.
              </Typography>
            )}
            <FHSmartTextarea
              id={`goal_description_rationale-${goal.id}`}
              data-testid="descriptionTextField"
              colorTheme={Themes.Primary}
              coloredBackground
              hightlightOnDetection
              heightlightColour={FORTA_YELLOW}
              blackListedWordsPhrases={targetDescriptionBlacklist}
              maxRows={4.5}
              minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
              disabled={!isEditable}
              expandable={isEditable}
              placeholder="Enter description of goal"
              value={descriptionValues.description}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                handleTextFieldChange('description', e.target.value, false);
                if (
                  e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH ||
                  e.target.value?.includes('{') ||
                  e.target.value?.includes('}')
                ) {
                  setErrorState((prev: any) => ({
                    ...prev,
                    description: true,
                  }));
                } else {
                  setErrorState((prev: any) => ({
                    ...prev,
                    description: false,
                  }));
                }
              }}
              onBlur={(e) => {
                handleTextFieldBlur('description', e.target.value);
              }}
              onCloseExpandDialog={() => {
                handleTextFieldBlur('description', descriptionValues.description);
              }}
              error={errorState && errorState['description']}
              helperText={
                errorState && errorState['description']
                  ? descriptionValues.description.length < MINIMUM_REQUIRED_SENTENCE_LENGTH
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                  : ''
              }
            />
          </>
        ) : descriptionValues.description === '' || descriptionValues.description === null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <ErrorOutline sx={{ color: FORTA_ERROR }} />
            <Typography color={FORTA_ERROR} align="left">
              Not available - Goal description is required
            </Typography>
          </Box>
        ) : (
          <Typography align="left">{descriptionValues.description}</Typography>
        )}
      </Box>
    </Box>
  );
}

export default GoalDescriptions;
